import { Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { IProductDataV3 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import uniq from 'lodash-es/uniq';
import { Product } from '../../catalog/product/product.decorator';
import throttle from 'lodash-es/throttle';
import { TimeoutService } from '@df/ng/timeout.service';

declare global {
    interface Window {
        ratingSnippet?: (string, Object) => void;
        ReviewsWidget?: (string, Object) => void;
        carouselInlineWidget?: (string, Object) => void;
    }
}

@Injectable()
export class ReviewsIOService {
    constructor(
        private requestService: RequestService,
        private app: App,
        private timeoutService: TimeoutService
    ) {}

    async load(): Promise<void> {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        this.loadRatingScript();
        this.loadCarouselInlineScript();
    }

    private async loadRatingScript(): Promise<HTMLScriptElement> {
        this.requestService.loadCss('https://widget.reviews.io/rating-snippet/dist.css');
        return this.requestService.loadScriptAsync(`https://widget.reviews.io/rating-snippet/dist.js`);
    }

    private showRatingText: boolean = true;

    updateRatingText(showRatingText: boolean): void {
        this.showRatingText = showRatingText;
    }

    throttledUpdateRatingSnippet = this.timeoutService.throttle(() => {
        this.updateRatingSnippet(this.showRatingText);
    }, 200, { leading: false, trailing: true });

    async updateRatingSnippet(showRatingText: boolean): Promise<void> {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        if (!window.ratingSnippet) {
            await this.loadRatingScript();
        }
        window.ratingSnippet!('ruk_rating_snippet', {
            store: 'barker-and-stonehouse' + (this.app.dfConfig.environment === 'production' ? '' : '1'),
            mode: 'default',
            color: '#0E1311',
            linebreak: false,
            text: showRatingText ? 'Reviews' : undefined,
            singularText: showRatingText ? 'Review' : undefined,
            lang: 'en',
            usePolaris: true,
            showEmptyStars: false
        });
    }

    private async loadPolarisScript(): Promise<HTMLScriptElement> {
        return this.requestService.loadScriptAsync(`https://widget.reviews.io/polaris/build.js`);
    }

    async updatePolaris(product: Product): Promise<void> {
        const SKUs = this.getProductsSKUs(product);

        if (!window.ReviewsWidget) {
            await this.loadPolarisScript();
        }
        window.ReviewsWidget!('#ReviewsWidget', {
            //Your REVIEWS.io Store ID and widget type:
            store: 'barker-and-stonehouse' + (this.app.dfConfig.environment === 'production' ? '' : '1'),
            widget: 'polaris',

            //Content settings (store_review,product_review,third_party_review,questions). Choose what to display in this widget:
            options: {
                types: 'product_review,questions',
                lang: 'en',
                //Possible layout options: bordered, large and reverse.
                layout: '',
                //How many reviews & questions to show per page?
                per_page: 5,
                store_review: {
                    hide_if_no_results: false
                },
                third_party_review: {
                    hide_if_no_results: false
                },
                //Product specific settings. Provide product SKU for which reviews should be displayed:
                product_review: {
                    //Display product reviews - include multiple product SKUs seperated by Semi-Colons (Main Indentifer in your product catalog )
                    sku: SKUs,
                    hide_if_no_results: false
                },
                //Questions settings:
                questions: {
                    hide_if_no_results: false,
                    enable_ask_question: true,
                    show_dates: true,
                    //Display group questions by providing a grouping variable, new questions will be assigned to this group.
                    grouping: '[Group questions by providing a grouping variable here or a specific product SKU]'
                },
                //Header settings:
                header: {
                    enable_summary: true, //Show overall rating & review count
                    enable_ratings: true,
                    enable_attributes: true,
                    enable_image_gallery: true, //Show photo & video gallery
                    enable_percent_recommended: false, //Show what percentage of reviewers recommend it
                    enable_write_review: true, //Show "Write Review" button
                    enable_ask_question: true, //Show "Ask Question" button
                    enable_sub_header: false, //Show subheader
                    rating_decimal_places: 2,
                    use_write_review_button: false //Show "be the first to leave a review" text as a button
                },

                //Filtering settings:
                filtering: {
                    enable: true, //Show filtering options
                    enable_text_search: true, //Show search field
                    enable_sorting: true, //Show sorting options (most recent, most popular)
                    enable_product_filter: false, //Show product options filter
                    enable_media_filter: true, //Show reviews with images/video/media options
                    enable_overall_rating_filter: true, //Show overall rating breakdown filter
                    enable_language_filter: false, // Filter by review language
                    enable_language_filter_language_change: false, // Update widget language based on language selected
                    enable_ratings_filters: true, //Show product attributes filter
                    enable_attributes_filters: true, //Show author attributes filter
                    enable_expanded_filters: false //Show filters as a section instead of dropdown
                },

                //Review settings:
                reviews: {
                    enable_avatar: true, //Show author avatar
                    enable_reviewer_name: true, //Show author name
                    enable_reviewer_address: true, //Show author location
                    reviewer_address_format: 'city, country', //Author location display format
                    enable_verified_badge: true, //Show "Verified Customer" badge
                    enable_subscriber_badge: true, //Show "Verified Subscriber" badge
                    review_content_filter: 'undefined', //Filter content
                    enable_reviewer_recommends: true, //Show "I recommend it" badge
                    enable_attributes: true, //Show author attributes
                    enable_product_name: true, //Show display product name
                    enable_review_title: undefined, //Show review title
                    enable_replies: true, //Show review replies
                    enable_images: true, //Show display review photos
                    enable_ratings: true, //Show product attributes (additional ratings)
                    enable_share: true, //Show share buttons
                    enable_helpful_vote: true, //Show "was this helpful?" section
                    enable_helpful_display: true, //Show how many times times review upvoted
                    enable_report: true, //Show report button
                    enable_date: true, //Show when review was published
                    enable_third_party_source: true, //Show third party source
                    enable_duplicate_reviews: undefined //Show multiple reviews from the same user
                }
            },
            //Translation settings
            translations: {
                'Verified Customer': 'Verified Customer'
            },
            //Style settings:
            styles: {
                //Base font size is a reference size for all text elements. When base value gets changed, all TextHeading and TexBody elements get proportionally adjusted.
                '--base-font-size': '16px',

                //Button styles (shared between buttons):
                '--common-button-font-family': 'inherit',
                '--common-button-font-size': '16px',
                '--common-button-font-weight': '500',
                '--common-button-letter-spacing': '0',
                '--common-button-text-transform': 'none',
                '--common-button-vertical-padding': '10px',
                '--common-button-horizontal-padding': '20px',
                '--common-button-border-width': '2px',
                '--common-button-border-radius': '0px',

                //Primary button styles:
                '--primary-button-bg-color': '#0E1311',
                '--primary-button-border-color': '#0E1311',
                '--primary-button-text-color': '#ffffff',

                //Secondary button styles:
                '--secondary-button-bg-color': 'transparent',
                '--secondary-button-border-color': '#0E1311',
                '--secondary-button-text-color': '#0E1311',

                //Star styles:
                '--common-star-color': '#0E1311',
                '--common-star-disabled-color': 'rgba(0,0,0,0.25)',
                '--medium-star-size': '22px',
                '--small-star-size': '19px',

                //Heading styles:
                '--heading-text-color': '#0E1311',
                '--heading-text-font-weight': '600',
                '--heading-text-font-family': 'inherit',
                '--heading-text-line-height': '1.4',
                '--heading-text-letter-spacing': '0',
                '--heading-text-transform': 'none',

                //Body text styles:
                '--body-text-color': '#0E1311',
                '--body-text-font-weight': '400',
                '--body-text-font-family': 'inherit',
                '--body-text-line-height': '1.4',
                '--body-text-letter-spacing': '0',
                '--body-text-transform': 'none',

                //Input field styles:
                '--inputfield-text-font-family': 'inherit',
                '--input-text-font-size': '14px',
                '--inputfield-text-font-weight': '400',
                '--inputfield-text-color': '#0E1311',
                '--inputfield-border-color': 'rgba(0,0,0,0.2)',
                '--inputfield-background-color': 'transparent',
                '--inputfield-border-width': '1px',
                '--inputfield-border-radius': '0px',
                '--common-border-color': 'rgba(0,0,0,0.15)',
                '--common-border-width': '1px',
                '--common-sidebar-width': '190px',

                //Filters panel styles:
                '--filters-panel-bg-color': 'transparent',
                '--filters-panel-font-size': '16px',
                '--filters-panel-text-color': '16px',
                '--filters-panel-horizontal-padding': '0',
                '--filters-panel-vertical-padding': '0',

                //Slider indicator (for attributes) styles:
                '--slider-indicator-bg-color': 'rgba(0,0,0,0.1)',
                '--slider-indicator-button-color': '#0E1311',
                '--slider-indicator-width': '190px',

                //Badge styles:
                '--badge-icon-color': '#0E1311',
                '--badge-icon-font-size': 'inherit',
                '--badge-text-color': '#0E1311',
                '--badge-text-font-size': 'inherit',
                '--badge-text-letter-spacing': 'inherit',
                '--badge-text-transform': 'inherit',

                //Author styles:
                '--author-font-size': 'inherit',
                '--author-text-transform': 'none',

                //Author avatar styles:
                '--avatar-thumbnail-size': '60px',
                '--avatar-thumbnail-border-radius': '100px',
                '--avatar-thumbnail-text-color': '#0E1311',
                '--avatar-thumbnail-bg-color': 'rgba(0,0,0,0.1)',

                //Product photo or review photo styles:
                '--photo-video-thumbnail-size': '80px',
                '--photo-video-thumbnail-border-radius': '0px',

                //Media (photo & video) slider styles:
                '--mediaslider-scroll-button-icon-color': '#0E1311',
                '--mediaslider-scroll-button-bg-color': 'rgba(255, 255, 255, 0.85)',
                '--mediaslider-overlay-text-color': '#ffffff',
                '--mediaslider-overlay-bg-color': 'rgba(0, 0, 0, 0.8))',
                '--mediaslider-item-size': '110px',

                //Pagination & tabs styles (normal):
                '--pagination-tab-text-color': '#0E1311',
                '--pagination-tab-text-transform': 'none',
                '--pagination-tab-text-letter-spacing': '0',
                '--pagination-tab-text-font-size': '16px',
                '--pagination-tab-text-font-weight': '600',

                //Pagination & tabs styles (active):
                '--pagination-tab-active-text-color': '#0E1311',
                '--pagination-tab-active-text-font-weight': '600',
                '--pagination-tab-active-border-color': '#0E1311',
                '--pagination-tab-border-width': '3px'
            }
        });
    }

    private async loadCarouselInlineScript(): Promise<HTMLScriptElement> {
        this.requestService.loadCss('https://assets.reviews.io/css/widgets/carousel-widget.css');
        this.requestService.loadCss('https://assets.reviews.io/iconfont/reviewsio-icons/style.css');
        return this.requestService.loadScriptAsync(`https://widget.reviews.io/carousel-inline-iframeless/dist.js`);
    }

    async updateCarousel(product: Product): Promise<void> {
        const SKUs = this.getProductsSKUs(product);

        if (!window.carouselInlineWidget) {
            await this.loadCarouselInlineScript();
        }

        window.carouselInlineWidget!('reviewsio-carousel-widget', {
            store: 'barker-and-stonehouse' + (this.app.dfConfig.environment === 'production' ? '' : '1'),

            sku: SKUs,

            lang: 'en',

            carousel_type: 'default',

            styles_carousel: 'CarouselWidget--sideHeader--withcards',

            /*Widget settings:*/

            options: {
                general: {
                    /*What reviews should the widget display? Available options: company, product, third_party. You can choose one type or multiple separated by comma.*/

                    review_type: 'product',

                    /*Minimum number of reviews required for widget to be displayed*/

                    min_reviews: '1',

                    /*Maximum number of reviews to include in the carousel widget.*/

                    max_reviews: '20',

                    address_format: 'CITY, COUNTRY',

                    /*Carousel auto-scrolling speed. 3000 = 3 seconds. If you want to disable auto-scroll, set this value to false.*/

                    enable_auto_scroll: 10000
                },

                header: {
                    /*Show overall rating stars*/

                    enable_overall_stars: true,

                    rating_decimal_places: 2
                },

                reviews: {
                    /*Show customer name*/

                    enable_customer_name: true,

                    /*Show customer location*/

                    enable_customer_location: true,

                    /*Show "verified review" badge*/

                    enable_verified_badge: true,

                    /*Show "verified subscriber" badge*/

                    enable_subscriber_badge: true,

                    /*Show "I recommend this product" badge (Only for product reviews)*/

                    enable_recommends_badge: true,

                    /*Show photos attached to reviews*/

                    enable_photos: true,

                    /*Show videos attached to reviews*/

                    enable_videos: true,

                    /*Show when review was written*/

                    enable_review_date: true,

                    /*Hide reviews written by the same customer (This may occur when customer reviews multiple products)*/

                    disable_same_customer: true,

                    /*Minimum star rating*/

                    min_review_percent: 4,

                    /*Show 3rd party review source*/

                    third_party_source: true,

                    /*Hide reviews without comments (still shows if review has a photo)*/

                    hide_empty_reviews: true,

                    /*Show product name*/

                    enable_product_name: true,

                    /*Show only reviews which have specific tags (multiple semicolon separated tags allowed i.e tag1;tag2)*/

                    tags: '',

                    /*Show branch, only one input*/

                    branch: '',

                    enable_branch_name: false
                },

                popups: {
                    /*Make review items clickable (When they are clicked, a popup appears with more information about a customer and review)*/

                    enable_review_popups: true,

                    /*Show "was this review helpful" buttons*/

                    enable_helpful_buttons: true,

                    /*Show how many times review was upvoted as helpful*/

                    enable_helpful_count: true,

                    /*Show share buttons*/

                    enable_share_buttons: true
                }
            },

            translations: {
                verified_customer: 'Verified Customer'
            },

            styles: {
                /*Base font size is a reference size for all text elements. When base value gets changed, all TextHeading and TexBody elements get proportionally adjusted.*/

                '--base-font-size': '16px',

                '--base-maxwidth': '100%',

                /*Logo styles:*/

                '--reviewsio-logo-style': 'var(--logo-inverted)',

                /*Star styles:*/

                '--common-star-color': ' #0E1311',

                '--common-star-disabled-color': ' rgba(0,0,0,0.25)',

                '--medium-star-size': ' 22px',

                '--small-star-size': '19px' /*Modal*/,

                '--x-small-star-size': '16px',

                '--x-small-star-display': 'inline-flex',

                /*Header styles:*/

                '--header-order': '1',

                '--header-width': '280px',

                '--header-bg-start-color': '#333',

                '--header-bg-end-color': '#333',

                '--header-gradient-direction': '135deg',

                '--header-padding': '1.5em',

                '--header-border-width': '0px',

                '--header-border-color': 'rgba(0,0,0,0.1)',

                '--header-border-radius': '0px',

                '--header-shadow-size': '10px',

                '--header-shadow-color': 'rgba(0, 0, 0, 0.05)',

                /*Header content styles:*/

                '--header-star-color': '#ffffff',

                '--header-disabled-star-color': 'inherit',

                '--header-heading-text-color': '#ffffff',

                '--header-heading-font-size': 'inherit',

                '--header-heading-font-weight': 'inherit',

                '--header-heading-line-height': 'inherit',

                '--header-heading-text-transform': 'inherit',

                '--header-subheading-text-color': '#ffffff',

                '--header-subheading-font-size': 'inherit',

                '--header-subheading-font-weight': '300',

                '--header-subheading-line-height': 'inherit',

                '--header-subheading-text-transform': 'inherit',

                /*Review item styles:*/

                '--item-maximum-columns': '5' /*Must be 3 or larger*/,

                '--item-background-start-color': '#ffffff',

                '--item-background-end-color': '#ffffff',

                '--item-gradient-direction': '135deg',

                '--item-padding': '1.5em',

                '--item-border-width': '0px',

                '--item-border-color': 'rgba(0,0,0,0.1)',

                '--item-border-radius': '0px',

                '--item-shadow-size': '10px',

                '--item-shadow-color': 'rgba(0,0,0,0.05)',

                /*Heading styles:*/

                '--heading-text-color': ' #0E1311',

                '--heading-text-font-weight': ' 600',

                '--heading-text-font-family': ' inherit',

                '--heading-text-line-height': ' 1.4',

                '--heading-text-letter-spacing': '0',

                '--heading-text-transform': 'none',

                /*Body text styles:*/

                '--body-text-color': ' #0E1311',

                '--body-text-font-weight': '400',

                '--body-text-font-family': ' inherit',

                '--body-text-line-height': ' 1.4',

                '--body-text-letter-spacing': '0',

                '--body-text-transform': 'none',

                /*Scroll button styles:*/

                '--scroll-button-icon-color': '#0E1311',

                '--scroll-button-icon-size': '24px',

                '--scroll-button-bg-color': 'transparent',

                '--scroll-button-border-width': '0px',

                '--scroll-button-border-color': 'rgba(0,0,0,0.1)',

                '--scroll-button-border-radius': '60px',

                '--scroll-button-shadow-size': '0px',

                '--scroll-button-shadow-color': 'rgba(0,0,0,0.1)',

                '--scroll-button-horizontal-position': '3px',

                '--scroll-button-vertical-position': '0px',

                /*Badge styles:*/

                '--badge-icon-color': '#0E1311',

                '--badge-icon-font-size': '15px',

                '--badge-text-color': '#0E1311',

                '--badge-text-font-size': 'inherit',

                '--badge-text-letter-spacing': 'inherit',

                '--badge-text-transform': 'inherit',

                /*Author styles:*/

                '--author-font-size': 'inherit',

                '--author-font-weight': 'inherit',

                '--author-text-transform': 'inherit',

                /*Product photo or review photo styles:*/

                '--photo-video-thumbnail-size': '60px',

                '--photo-video-thumbnail-border-radius': '0px',

                /*Popup styles:*/

                '--popup-backdrop-color': 'rgba(0,0,0,0.75)',

                '--popup-color': '#ffffff',

                '--popup-star-color': 'inherit',

                '--popup-disabled-star-color': 'inherit',

                '--popup-heading-text-color': 'inherit',

                '--popup-body-text-color': 'inherit',

                '--popup-badge-icon-color': 'inherit',

                '--popup-badge-icon-font-size': '19px',

                '--popup-badge-text-color': 'inherit',

                '--popup-badge-text-font-size': '14px',

                '--popup-border-width': '0px',

                '--popup-border-color': 'rgba(0,0,0,0.1)',

                '--popup-border-radius': '0px',

                '--popup-shadow-size': '0px',

                '--popup-shadow-color': 'rgba(0,0,0,0.1)',

                '--popup-icon-color': '#0E1311',

                /*Tooltip styles:*/

                '--tooltip-bg-color': '#0E1311',

                '--tooltip-text-color': '#ffffff'
            }
        });
    }

    getProductsSKUs(product: Product): string {
        return (
            (
                uniq(
                    this.app.resources.product
                        .getAllExisting(
                            this.app.resources.product
                                .toIds(product)
                                .concat((product.parent as Product<IProductDataV3> | undefined)?.data?.link?.crossSell || [])
                                .concat((product as Product<IProductDataV3>).data?.link?.crossSell || [])
                        )
                        .concat(product.parent.children)
                        .concat([product.parent])
                        .map(product => product.data?.sku)
                ) as string[]
            ).join(';') || ''
        );
    }
}